'use client'

import React, { useCallback, useEffect, useState } from 'react'
import Image from 'next/image'
import useEmblaCarousel from 'embla-carousel-react'
import { EmblaCarouselType } from 'embla-carousel'
import { useThrottledCallback } from 'use-debounce'
import AnimatedSection from '@/components/AnimatedSection'
import styles from './styles.module.css'

interface Stage {
  number: string
  title: string
  description: string
}

const WhyChooseUs: React.FC = () => {
  const stages: Stage[] = [
    {
      number: '01',
      title: 'Комплексный подход',
      description: 'Мы детально изучаем ваши задачи, проектируем логику и предлагаем идеи для развития. Вы получаете инструмент, который реально помогает вашему бизнесу расти.'
    },
    {
      number: '02',
      title: 'Выделенная команда',
      description: 'Подбираем оптимальный состав команды разработчиков с опытом в вашей нише. Это гарантирует чистый код и проверенные решения для вашей аудитории.'
    },
    {
      number: '03',
      title: 'Всегда на связи',
      description: 'Мы всегда на связи и строго соблюдаем договорённости по сроку. В случае критических ситуаций в работе сайта помогаем их решить даже в выходные.'
    }
  ]

  const [emblaRef, emblaApi] = useEmblaCarousel({
    dragFree: true,
    align: 'start',
    breakpoints: { '(min-width: 1440px)': { active: false } },
  })

  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true)
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true)

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setPrevBtnDisabled(!emblaApi.canScrollPrev())
    setNextBtnDisabled(!emblaApi.canScrollNext())
  }, [])
  
  const onSelectThrottled = useThrottledCallback(onSelect, 100)

  useEffect(() => {
    if (!emblaApi) return

    onSelect(emblaApi)
    emblaApi.on('reInit', onSelect).on('select', onSelect)
  }, [emblaApi, onSelect, onSelectThrottled])

  const onPrevButtonClick = useCallback(() => {
    if (!emblaApi) return
    emblaApi.scrollPrev()
  }, [emblaApi])

  const onNextButtonClick = useCallback(() => {
    if (!emblaApi) return
    emblaApi.scrollNext()
  }, [emblaApi])

  return (
    <section className={styles.whyChooseUs}>
      <AnimatedSection animation="slideUp">
        <div className={styles.subtitle}>Почему выбирают нас</div>
        <h2 
          className={styles.title}
          dangerouslySetInnerHTML={{ __html: '<span>100% клиентов продолжают развивать</span> свои проекты с нами' }}
        />
      </AnimatedSection>
      <AnimatedSection animation="slideUp">
        <div className={styles.stagesSlider}>
          <div className={styles.stagesSliderContainer}>
            <div className={styles.embla}>
              <div className={styles.embla__viewport} ref={emblaRef}>
                <div className={styles.embla__container}>
                  {stages.map((stage, index) => {
                    const imagePaths = [
                      '/images/complex-solutions/why_choose_us_1.svg',
                      '/images/complex-solutions/why_choose_us_2.svg',
                      '/images/complex-solutions/why_choose_us_3.svg'
                    ]
                    
                    return (
                      <div key={index} className={styles.embla__slide}>
                        <div className={styles.stageCard}>
                          <div className={styles.stageNumber}>
                            {stage.number}
                          </div>
                          <div className={styles.stageImage}>
                            <Image
                              src={imagePaths[index]}
                              alt={stage.title}
                              width={310}
                              height={200}
                              className={styles.image}
                            />
                          </div>
                          <h3 className={styles.stageTitle} dangerouslySetInnerHTML={{ __html: stage.title }}></h3>
                          <p className={styles.stageDescription}>{stage.description}</p>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
              
              {!prevBtnDisabled && (
                <button 
                  className={styles.embla__prev}
                  onClick={onPrevButtonClick}
                >
                  <Image
                    src="/images/portfolio/lk-agro-b2b/slider-button-left.svg"
                    alt="Previous"
                    width={34}
                    height={34}
                  />
                </button>
              )}
              
              {!nextBtnDisabled && (
                <button 
                  className={styles.embla__next}
                  onClick={onNextButtonClick}
                >
                  <Image
                    src="/images/portfolio/lk-agro-b2b/slider-button-right.svg"
                    alt="Next"
                    width={34}
                    height={34}
                  />
                </button>
              )}
            </div>
          </div>
        </div>
      </AnimatedSection>
    </section>
  )
}

WhyChooseUs.displayName = 'WhyChooseUs'

export default WhyChooseUs